/**
 * Click-anywhere-to-edit for the editor canvas.
 *
 * The page the author sees is a `.cdm-editor` box with real page margins and a minimum height, but
 * the editable ProseMirror element inside it is only as tall as the text. Everything else — the four
 * margins and the empty space below the last line — is the page div's own area, so a click there hit
 * nothing editable and the editor never took focus: a one-line document meant aiming at that one
 * line to start typing.
 *
 * ProseMirror's own `posAtCoords` answers "what position is under this point", but only for points
 * inside the content box. The work here is deciding what a click OUTSIDE it means, which is what
 * every word processor already has an answer for: clamp the point onto the nearest edge of the
 * content and use the position there. Clicking a left margin puts the caret at the start of the line
 * beside it; clicking below the text puts it at the end of the document.
 */
import type { Editor } from '@tiptap/react'

/**
 * Focuses the editor at the position nearest to a viewport point, and returns what it did — the
 * caller has no other way to tell an exact hit from a clamped one, and the tests assert on it.
 *
 * `'none'` means the editor was not focusable at all (destroyed, or not yet mounted), and the caller
 * should leave the event alone rather than swallow it.
 */
export function focusNearestPosition(
  editor: Editor | null | undefined,
  clientX: number,
  clientY: number,
): 'exact' | 'clamped' | 'start' | 'end' | 'none' {
  if (!editor || editor.isDestroyed) return 'none'

  const view = editor.view
  if (!view?.dom) return 'none'

  const exact = safePosAtCoords(view, clientX, clientY)
  if (exact !== null) {
    editor.commands.focus(exact)
    return 'exact'
  }

  const rect = view.dom.getBoundingClientRect()

  // Above the first line or below the last: the document's own ends, which is what a click in the
  // top or bottom margin means. Checked before the horizontal clamp because a click in a corner is
  // vertically outside as well as horizontally, and "end of document" reads better there than
  // "end of whichever line happens to be nearest".
  if (clientY < rect.top) {
    editor.commands.focus('start')
    return 'start'
  }
  if (clientY > rect.bottom) {
    editor.commands.focus('end')
    return 'end'
  }

  // Beside the text: same height, clamped horizontally onto the content box, so the caret lands at
  // the start or end of the line the author clicked next to. The 1px inset keeps the point inside
  // the box rather than exactly on its boundary, which posAtCoords can report as outside.
  const clampedX = Math.min(Math.max(clientX, rect.left + 1), rect.right - 1)
  const clamped = safePosAtCoords(view, clampedX, clientY)
  if (clamped !== null) {
    editor.commands.focus(clamped)
    return 'clamped'
  }

  // An empty or zero-height content box (a document still loading) leaves nothing to clamp onto.
  editor.commands.focus('end')
  return 'end'
}

/**
 * `posAtCoords` returns null outside the content, but it can also throw on a view whose DOM is
 * mid-teardown — and a click handler that throws would break the page rather than just miss a
 * position.
 */
function safePosAtCoords(view: Editor['view'], left: number, top: number): number | null {
  try {
    return view.posAtCoords({ left, top })?.pos ?? null
  } catch {
    return null
  }
}
